import { useSetRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import { history, useFetchWrapper } from '../_helpers';
import { authAtom, usersAtom } from '../_state';

import { addDataIntoCache, deleteCache } from '../../Portal/Cache/cache';
import { isAlertCollectionsOpenAtom, isAlertContractsOpenAtom, collectionsAtom, recentCollectionsAtom, justDownloadedCollectionsAtom, listCollectionsByAffaireAtom, isListCollectionsByAffaireLoadedAtom, areCollectionsLoadedAtom, documentMetaListAtom, uploadedDocumentsAtom, justSentDocsAtom, areUploadedDocumentsLoadedAtom, areUploadedDocumentsLoadingAtom, externalVerifDocumentsAtom, idCollectionsDownloadingAtom, idNavettesDownloadingAtom, areExternalVerifDocumentsLoadedAtom } from '../../Portal/LoadData/LoadData';

import { Role } from '../../Portal/Useful/enum';
import { userAtom } from '../../Portal/RecoilApp';

import Loc from '../../Loc/Loc';

export { useUserActions };

function useUserActions () {
    const baseUrl = `${process.env.REACT_APP_API_URL}`;
    const baseUrlUsers = `${process.env.REACT_APP_API_URL}/users`;
    const baseUrlMetadata = `${process.env.REACT_APP_API_URL}/Metadata`;
    const baseUrlDocument = `${process.env.REACT_APP_API_URL}/Document`;
    const fetchWrapper = useFetchWrapper();
    const auth = useRecoilValue(authAtom);
    const setAuth = useSetRecoilState(authAtom);
    const setUsers = useSetRecoilState(usersAtom);
    const setCollections = useSetRecoilState(collectionsAtom);
    const resetCollections = useResetRecoilState(collectionsAtom);
    const resetRecentCollections = useResetRecoilState(recentCollectionsAtom);
    const resetJustDownloadedCollections = useResetRecoilState(justDownloadedCollectionsAtom);
    const resetListCollectionsByAffaire = useResetRecoilState(listCollectionsByAffaireAtom);
    const resetIsListCollectionsByAffaireLoaded = useResetRecoilState(isListCollectionsByAffaireLoadedAtom);
    const resetAreCollectionsLoaded = useResetRecoilState(areCollectionsLoadedAtom);
    const resetStateAlertCollections = useResetRecoilState(isAlertCollectionsOpenAtom);
    const resetStateAlertContracts = useResetRecoilState(isAlertContractsOpenAtom);
    const resetDocumentMetaList = useResetRecoilState(documentMetaListAtom);
    const resetUploadedDocuments = useResetRecoilState(uploadedDocumentsAtom);
    const resetJustSentDocs = useResetRecoilState(justSentDocsAtom);
    const resetAreUploadedDocumentsLoaded = useResetRecoilState(areUploadedDocumentsLoadedAtom);
    const resetAreUploadedDocumentsLoading = useResetRecoilState(areUploadedDocumentsLoadingAtom);
    const resetExternalVerifDocuments = useResetRecoilState(externalVerifDocumentsAtom);
    const resetIdCollectionsDownloading = useResetRecoilState(idCollectionsDownloadingAtom);
    const resetIdNavettesDownloading = useResetRecoilState(idNavettesDownloadingAtom);
    const resetAreExternalDocsLoaded = useResetRecoilState(areExternalVerifDocumentsLoadedAtom);
    const resetUser = useResetRecoilState(userAtom);

    return {
        login,
        logout,
        getAll,
        updatePassword,
        isTokenValid,
        getCollections,
        getRecentCollections,
        getNavettes,
        getAffaires,
        download,
        getDocumentMetaList,
        updateCollections,
        getUploadedDocuments,
        getExternalVerifDocuments,
        updateExternalDoc,
        sendCodeMail
    }

    function login(username, password) {
        return fetchWrapper.post(`${baseUrlUsers}/authenticate`, { username, password })
            .then(user => {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(user));
                setAuth(user);

                // get return url from location state or default to home page
                const { from } = history.location.state || { from: { pathname: '/' } };
                history.push(from);
            });
    }

    function logout() {
        // remove user from local storage, set auth state to null and redirect to login page
        localStorage.removeItem('user');
        ['collections', 'listeCollectionsParAffaire'].forEach(cacheName => deleteCache(cacheName));
        resetAllAtoms();
        setAuth(null);
        history.push('/portail_V2/login');
    }

    function getAll() {
        return fetchWrapper.get(baseUrlUsers).then(setUsers);
    }

    function updatePassword(username, password, id) {
        return fetchWrapper.put(`${baseUrlUsers}/update/` + id, { username, password })
    }

    function isTokenValid(user) {
        if (user.role >= Role.PermUser) {
            if (user.role === Role.PermUser) {
                return fetchWrapper.post(`${baseUrlUsers}/secretActionForUser`);
            }
            return fetchWrapper.post(`${baseUrlUsers}/secretActionForUserTmp`);
        }
        return Promise.reject("Vérification de la connexion en cours...");
    }

    function updateCollections(collections, setAreCollectionsLoaded, setStateSwitchDownloadSelect) {
        const update = (collections) => {
            setCollections(collections);
            setAreCollectionsLoaded(true);
            setStateSwitchDownloadSelect && setStateSwitchDownloadSelect(true);
            addDataIntoCache('collections', auth.username, collections);
        }
        if (collections.length === 0) {
            update([]);
        }
        collections = collections.map((collection, index) => {
            collection.navettes = []; // stocker les navettes pour ne les charger qu'une fois
            collection.index = index; // définir un index à chaque navette pour pouvoir les modifier individuellement
            collection.navCategories = []; // stocker les catégories des navettes pour ne les définir qu'une fois
            collection.colCategories = [{label: Loc.trad().UserActionsRef, field: "reference"}, {label: Loc.trad().UserActionsDateSend, field: "dateEmission"}, {label: Loc.trad().UserActionsDateDownload, field: "dateDownload"}, {label: Loc.trad().UserActionsObservation, field: "observation"}, collection.additionnalMetas.map(data => {return { label: data.name, field: data.name }})].flat(); // les quatres premières catégories sont des catégories fixes
            collection.additionnalMetas.forEach(data => { collection[data.name] = data.value })
            return collection;
        })
        let navettesCollections = [];
        let updated = 0;
        collections.forEach((collection) => {
            getNavettes(collection, collections).then(res => {
                updated++;
                navettesCollections = res;
                if (updated === collections.length) {
                    update(navettesCollections);
                }
            })
        })
    }

    function getCollections(affaire) {
        if (affaire.libelle === Loc.trad().RecepDocsContractsAll) {
            return fetchWrapper.get(`${baseUrl}/Collections`);
        }
        return fetchWrapper.get(`${baseUrl}/Collections/` + affaire.libelle);
    }

    function getRecentCollections(setRecentCollections) {
        return fetchWrapper.get(`${baseUrl}/recent`)
            .then((collections) => {
                setRecentCollections(collections);
            })
    }

    function getNavettes(collection, collections) {
        return fetchWrapper.get(`${baseUrl}/Navettes/` + collection.id)
            .then(navettes => {
                navettes.forEach((nav, index) => nav.index = index);
                let categories = [{ label: Loc.trad().UserActionsName, field: "name" }];
                if (navettes.length > 0) {
                    navettes[0].concerneDoc.additionnalMetas.forEach((data) => {
                        const newCategory = { label: data.name === "Indice" ? Loc.trad().UserActionsID : data.name === "Status" ? Loc.trad().UserActionsStatus : data.name === "Commentary" ? Loc.trad().UserActionsComment : data.name, field: data.name };
                        categories.push(newCategory);
                    })
                }
                collection.navettes = navettes;
                collection.navCategories = categories;
                collections[collection.index] = collection;
                return Promise.resolve(collections);
            })
            .catch(() => { return Promise.resolve([]) })
    }

    function getAffaires() {
        return fetchWrapper.get(`${baseUrl}/Affaires`);
    }

    function download(type, id) {
        return fetchWrapper.get(`${baseUrl}` + "/"+ type + `/download/` + id);
    }

    function getDocumentMetaList() {
        return fetchWrapper.get(`${baseUrlMetadata}/getDocumentMetaList`);
    }

    function getUploadedDocuments(maxResultCount) {
        return fetchWrapper.get(`${baseUrlDocument}/` + maxResultCount);
    }

    function getExternalVerifDocuments() {
        return fetchWrapper.get(`${baseUrlDocument}/externalVerif`);
    }

    function updateExternalDoc(id, state) {
        return fetchWrapper.post(`${baseUrlDocument}/updateExternalDocument`, { id, state });
    }

    function sendCodeMail(hash, email) {
        return fetchWrapper.get(`${baseUrl}/Collections/send/` + hash + '/' + email);
    }

    function resetAllAtoms() {
        resetCollections();
        resetRecentCollections();
        resetJustDownloadedCollections();
        resetListCollectionsByAffaire();
        resetIsListCollectionsByAffaireLoaded();
        resetAreCollectionsLoaded();
        resetStateAlertCollections();
        resetStateAlertContracts();
        resetDocumentMetaList();
        resetUploadedDocuments();
        resetJustSentDocs();
        resetAreUploadedDocumentsLoaded();
        resetAreUploadedDocumentsLoading();
        resetExternalVerifDocuments();
        resetIdCollectionsDownloading();
        resetIdNavettesDownloading();
        resetAreExternalDocsLoaded();
        resetUser();
    }
}